import { useTranslation } from 'react-i18next';
import { Head } from '~/components/shared/Head';
import {
  useParams,
  Routes,
  Route,
  Link,
  useLocation,
  Navigate,
} from 'react-router-dom';
import { TemplatesModule } from './components/TemplatesModule';
import { CreateTemplateModule } from './components/CreateTemplateModule';
import { CampaignsModule } from './components/CampaignsModule';
import { AudiencesModule } from './components/AudiencesModule';
import { AnalyticsModule } from './components/AnalyticsModule';
import { useQuery } from '@apollo/client';
import { LIST_WHATSAPP_TEMPLATES } from '~/services/campaigns';
import { Discuss } from 'react-loader-spinner';
import { SignalSlashIcon } from '@heroicons/react/24/solid';

const CampaignsNavigation = () => {
  const [t] = useTranslation('campaigns');
  const { agentId } = useParams();
  const location = useLocation();

  const isActive = (path: string) => {
    const currentPath = location.pathname;
    // Get the last segment after campaigns/
    const pathSegments = currentPath.split('/campaigns/')[1]?.split('/')[0];
    return pathSegments === path;
  };

  const navItems = [
    { path: 'templates', label: t('nav_templates'), icon: '📝' },
    { path: 'campaigns', label: t('nav_campaigns'), icon: '🚀' },
    { path: 'audiences', label: t('nav_audiences'), icon: '👥' },
    { path: 'analytics', label: t('nav_analytics'), icon: '📊' },
  ];

  return (
    <div className="mb-6 border-b border-gray-200">
      <nav className="flex space-x-8">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={`/agent/${agentId}/campaigns/${item.path}`}
            className={`
              flex items-center space-x-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors
              ${
                isActive(item.path)
                  ? 'border-custom-primary-accent-600 text-custom-primary-accent-700'
                  : 'text-custom-surface-400 hover:border-base-300 border-transparent hover:text-gray-700'
              }
            `}
          >
            <span>{item.icon}</span>
            <span>{item.label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
};

const CampaignsContent = () => {
  const { agentId } = useParams();
  const [t] = useTranslation('campaigns');

  // Check if WhatsApp channel is configured
  const { data, loading, error } = useQuery(LIST_WHATSAPP_TEMPLATES, {
    variables: {
      data: {
        agent_id: agentId,
      },
    },
  });

  if (loading) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <Discuss
          visible
          height="80"
          width="80"
          ariaLabel="campaigns-loading"
          wrapperStyle={{}}
          wrapperClass="discuss-wrapper"
        />
      </div>
    );
  }

  const channel = data?.list_whatsapp_templates?.data?.channel;

  if (!channel) {
    return (
      <div className="flex h-64 w-full flex-col items-center justify-center text-center">
        <SignalSlashIcon className="fill-primary mb-4 h-12 w-12" />
        <h2 className="text-xl font-semibold text-gray-900">
          {t('no_channel_title')}
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          {t('no_channel_description')}
        </p>
        <div className="mt-6">
          <Link to={`/agent/${agentId}/settings/integrations`}>
            <button className="btn btn-primary rounded-full">
              {t('setup_whatsapp')}
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <CampaignsNavigation />
      <Routes>
        <Route index element={<Navigate to="templates" replace />} />
        <Route path="templates">
          <Route
            index
            element={<TemplatesModule agentId={agentId!} channel={channel} />}
          />
          <Route
            path="create"
            element={
              <CreateTemplateModule agentId={agentId!} channel={channel} />
            }
          />
        </Route>
        <Route
          path="campaigns/*"
          element={<CampaignsModule agentId={agentId!} channel={channel} />}
        />
        <Route
          path="audiences/*"
          element={<AudiencesModule agentId={agentId!} />}
        />
        <Route
          path="analytics/*"
          element={<AnalyticsModule agentId={agentId!} />}
        />
      </Routes>
    </>
  );
};

const Campaigns = () => {
  const [t] = useTranslation('campaigns');
  const { agentId } = useParams();

  if (!agentId) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-red-500">Agent ID is required</div>
      </div>
    );
  }

  return (
    <div className="m-auto w-full max-w-[1200px] px-8 pt-12">
      <Head title={t('campaigns_title')} />

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-medium text-gray-900">
          {t('campaigns_title')}
        </h1>
        <p className="mt-2 text-gray-600">{t('campaigns_description')}</p>
      </div>

      {/* Main Content */}
      <div className="bg-base-100 rounded-2xl p-6">
        <CampaignsContent />
      </div>
    </div>
  );
};

export default Campaigns;
